/**
 * Publishes the use of site elements as Shopify custom events, which the Customer events pixel
 * (CUSTOMER_EVENTS.md) forwards to GA4. Filters and sort live in filter-analytics.js and the size
 * guide in wf-size-guide-dialog.liquid.
 *
 * Listeners are delegated from the document, so elements that the theme renders or re-renders
 * later (carousels, the header panel, the gallery) are covered without hooking each component.
 *
 * Events:
 *   pdp_accordion_opened   { section, product_id }          a product page accordion opened by the shopper
 *   pdp_image_viewed       { position, total, product_id }  first time each gallery image (2nd onwards) is shown, per variant gallery
 *   recommendation_clicked { carousel, intent, position, product }  carousel: similar_items / recently_viewed / product_slider / recommendations
 *   shipping_info_clicked  { product_id }                   "See all options" under the add to cart
 *   store_selected         { store }                        store pages' location dropdown
 *   menu_clicked           { item, link, level }            menu panel, Women/Men and Sale; level: top_bar / panel / submenu
 */
(function () {
  const isProductPage = (window.ShopifyAnalytics?.meta?.page?.pageType ?? '') === 'product' || Boolean(window.ShopifyAnalytics?.meta?.product);

  /** @param {string} name @param {Record<string, unknown>} data */
  function publish(name, data) {
    try {
      window.Shopify?.analytics?.publish?.(name, data)?.catch?.(() => {});
    } catch (error) {
      // Analytics must never break the page.
    }
  }

  /** @param {Element | null | undefined} element */
  function text(element) {
    return (element?.textContent || element?.getAttribute('aria-label') || '').replace(/\s+/g, ' ').trim().slice(0, 100);
  }

  function productId() {
    return window.ShopifyAnalytics?.meta?.product?.id ?? null;
  }

  /** @param {Element} element 1-based position among its siblings */
  function position(element) {
    return element.parentElement ? Array.from(element.parentElement.children).indexOf(element) + 1 : 0;
  }

  /** @param {string} href */
  function pathname(href) {
    try {
      return new URL(href, window.location.origin).pathname;
    } catch (error) {
      return '';
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      // Product page accordions (Details & Care, About the Designer, Availability in store): the state
      // is read after the click, since the accordions stay open (disabled) on desktop
      const summary = target.closest('summary');
      const details = summary?.parentElement;
      if (isProductPage && details instanceof HTMLDetailsElement && details.closest('.wf-product-details')) {
        const wasOpen = details.open;
        setTimeout(() => {
          if (!wasOpen && details.open) publish('pdp_accordion_opened', { section: text(summary), product_id: productId() });
        });
        return;
      }

      const productLink = target.closest('a[href*="/products/"]');
      const carousel = productLink?.closest(
        'product-similar-items, product-recommendations, [data-recently-viewed-slider], .collection-product-slider'
      );
      if (productLink && carousel) {
        publish('recommendation_clicked', {
          carousel: carousel.matches('product-similar-items')
            ? 'similar_items'
            : carousel.matches('[data-recently-viewed-slider]')
              ? 'recently_viewed'
              : carousel.matches('.collection-product-slider')
                ? 'product_slider'
                : 'recommendations',
          intent: carousel.getAttribute('data-intent') || '',
          position: position(productLink.closest('.swiper-slide, li') || productLink),
          product: pathname(productLink.getAttribute('href') || ''),
        });
        return;
      }

      // The footer links to the same page: only the link in the product information counts
      if (isProductPage && target.closest('a[href="/pages/shipping"]') && !target.closest('footer, .shopify-section-group-footer-group')) {
        publish('shipping_info_clicked', { product_id: productId() });
        return;
      }

      const store = target.closest('.wf-store-location-select__link');
      if (store) {
        publish('store_selected', { store: text(store) });
        return;
      }

      // Menu only: the panel's links and buttons, the Women/Men openers and the Sale link (not the logo, search or cart)
      const menuItem = target.closest('#header-component')
        ? target.closest('balance-header-panel a, button[data-subpanel-target], button[data-header-panel-gender-open], a.header__sale-link')
        : null;
      if (menuItem) {
        const href = menuItem.getAttribute('href');
        publish('menu_clicked', {
          item: menuItem.getAttribute('data-header-panel-gender-open') || text(menuItem),
          link: href ? pathname(href) : '',
          level: menuItem.closest('[id^="subpanel-"]') ? 'submenu' : menuItem.closest('balance-header-panel') ? 'panel' : 'top_bar',
        });
      }
    },
    true
  );

  // Gallery images seen, once per image and page from the second one on (the first is always shown):
  // Swiper marks the shown slide with swiper-slide-active, also when the gallery is rebuilt for a variant.
  // Keyed by image, so another variant's images count again
  if (isProductPage && 'MutationObserver' in window) {
    const seen = new Set();
    /** @param {Element} slide */
    const imageKey = (slide) => (slide.querySelector('img')?.getAttribute('src') || '').split('?')[0] || `#${position(slide)}`;
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const slide = mutation.target;
        if (!(slide instanceof Element) || !slide.classList.contains('swiper-slide-active')) continue;
        const wrapper = slide.parentElement;
        if (!wrapper?.matches('.wf-pdp-media-gallery__swiper > .swiper-wrapper')) continue;
        const current = position(slide);
        const key = imageKey(slide);
        if (current === 1 || seen.has(key)) continue;
        seen.add(key);
        publish('pdp_image_viewed', { position: current, total: wrapper.children.length, product_id: productId() });
      }
    }).observe(document.body, { attributes: true, attributeFilter: ['class'], subtree: true });
  }
})();
